import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

import { useContextStore } from './contextStore';
import {
  cropConnectApiClient,
  DocumentFolder,
  DocumentSnapshotDto,
  DocumentSnapshotDtoListCropConnectApiResponse,
  DocumentSnapshotDTORetrievalResult,
} from '../services/cropConnectApiClient';

export interface DocumentSnapshotDTORetrievalResultWithUnread
  extends DocumentSnapshotDTORetrievalResult {
  unreadItemsCount: number;
}
export interface DocumentState {
  initializeDocumentsStore: () => Promise<DocumentSnapshotDto[]>;
  clearDocumentsStore: () => void;
  getDocumentsPage: (
    folder: DocumentFolder,
    top?: number,
    skip?: number,
    sortBy?: string,
    descending?: boolean
  ) => Promise<DocumentSnapshotDTORetrievalResult | null>;
  getLatestDocuments: (
    top?: number,
    sortBy?: string,
    descending?: boolean
  ) => Promise<DocumentSnapshotDto[]>;
  documents: { [key in DocumentFolder]: DocumentSnapshotDto[] };
  isUpdating: boolean;
  isInitializing: boolean;
  isContextUpdating: boolean;
  getDocumentsPageWithPrefilter: (
    folder: DocumentFolder,
    prefilter: string,
    top?: number,
    skip?: number,
    sortBy?: string,
    descending?: boolean
  ) => Promise<DocumentSnapshotDTORetrievalResult | null>;
  getAllInboxDocumentsPage: (
    country: string | undefined,
    top?: number,
    skip?: number,
    sortBy?: string,
    descending?: boolean
  ) => Promise<DocumentSnapshotDTORetrievalResultWithUnread | null>;
}

let initializeDocuments: Promise<DocumentSnapshotDto[]>;

const getDocumentsPageIntern = async (
  get: () => DocumentState,
  folder: DocumentFolder,
  top: number = 10,
  skip: number = 0,
  sortBy: string = 'postingDate',
  descending: boolean = true
) => {
  if (get().isInitializing) {
    await initializeDocuments;
  }

  const docFolder = get().documents[folder] ?? [];

  const page = docFolder
    .sort((a, b) => {
      const valueA = a[sortBy as keyof DocumentSnapshotDto];
      const valueB = b[sortBy as keyof DocumentSnapshotDto];

      //handling of undefined values
      if (valueA === undefined && valueB === undefined) return 0;
      if (valueA === undefined) return descending ? 1 : -1;
      if (valueB === undefined) return descending ? -1 : 1;

      //checksorting by a field that is a date
      if (sortBy === 'creationDate' || sortBy === 'postingDate') {
        //create Date objects from strings
        const dateA = valueA ? new Date(valueA as string) : undefined;
        const dateB = valueB ? new Date(valueB as string) : undefined;

        //precaution for incorrect dates
        if (!dateA && !dateB) return 0;
        if (!dateA) return descending ? 1 : -1;
        if (!dateB) return descending ? -1 : 1;

        //sorting first by year
        const yearA = dateA.getFullYear();
        const yearB = dateB.getFullYear();

        if (yearA !== yearB) {
          return descending ? yearB - yearA : yearA - yearB;
        }

        //sort by full date within the same year
        if (dateA < dateB) return descending ? 1 : -1;
        if (dateA > dateB) return descending ? -1 : 1;
        return 0;
      }

      //standard comparison for other fields
      if (valueA < valueB) return descending ? 1 : -1;
      if (valueA > valueB) return descending ? -1 : 1;

      return 0;
    })
    .slice(skip, skip + top);

  return {
    documents: page,
    availableItemsCount: docFolder.length,
  };
};

const clearDocuments = (
  set: <A extends string | { type: string }>(
    partial:
      | DocumentState
      | Partial<DocumentState>
      | ((state: DocumentState) => DocumentState | Partial<DocumentState>),
    replace?: boolean | undefined,
    action?: A | undefined
  ) => void
) => {
  set({
    documents: {} as {
      [key in DocumentFolder]: DocumentSnapshotDto[];
    },
  });
};

export const useDocumentsStore = create<DocumentState>()(
  devtools((set, get) => ({
    initializeDocumentsStore: () => {
      clearDocuments(set);

      const { contextUpdating } = useContextStore.getState();
      set({ isContextUpdating: contextUpdating });

      if (get().isInitializing) {
        return initializeDocuments;
      }

      initializeDocuments = new Promise<DocumentSnapshotDto[]>(
        (resolve, reject) => {
          cropConnectApiClient
            .loadDocuments(false)
            .then(
              (
                documentsResponse: DocumentSnapshotDtoListCropConnectApiResponse
              ) => {
                const docs = {} as {
                  [key in DocumentFolder]: DocumentSnapshotDto[];
                };

                for (const folder of Object.values(DocumentFolder)) {
                  const filtered =
                    documentsResponse.data?.filter(
                      (d) => d.documentFolder === folder
                    ) ?? [];

                  docs[folder] = filtered.sort((a, b) => {
                    const dateA = a.postingDate
                      ? new Date(a.postingDate).getTime()
                      : 0;
                    const dateB = b.postingDate
                      ? new Date(b.postingDate).getTime()
                      : 0;
                    return dateB - dateA;
                  });
                }

                set({ documents: docs });
                resolve(documentsResponse.data!);
              }
            )
            .catch((error) => {
              console.error('Error fetching documents:', error);
              reject(error);
            })
            .finally(() => {
              set({ isInitializing: false });
            });
        }
      );
      set({ isInitializing: true });
      return initializeDocuments;
    },

    getDocumentsPage: async (
      folder: DocumentFolder,
      top: number = 100,
      skip: number = 0,
      sortBy: string = 'postingDate',
      descending: boolean = true
    ): Promise<DocumentSnapshotDTORetrievalResult | null> => {
      return await getDocumentsPageIntern(
        get,
        folder,
        top,
        skip,
        sortBy,
        descending
      );
    },

    getLatestDocuments: async (
      top: number = 3,
      sortBy: string = 'postingDate',
      descending: boolean = true
    ): Promise<DocumentSnapshotDto[]> => {
      const allDocuments = Object.values(get().documents)
        .flat()
        .map((doc) => {
          const dateValue = doc[sortBy as keyof DocumentSnapshotDto];
          const date =
            dateValue && typeof dateValue === 'string'
              ? new Date(dateValue)
              : new Date(0); // Handle undefined or invalid dates
          return { ...doc, date };
        })
        .sort((a, b) => {
          const dateA = a.date.getTime();
          const dateB = b.date.getTime();
          return descending ? dateB - dateA : dateA - dateB;
        });

      return allDocuments.slice(0, top);
    },
    getDocumentsPageWithPrefilter: async (
      folder: DocumentFolder,
      prefilter: string,
      top: number = 100,
      skip: number = 0,
      sortBy: string = 'creationDate',
      descending: boolean = true
    ): Promise<DocumentSnapshotDTORetrievalResult | null> => {
      if (get().isInitializing) {
        await initializeDocuments;
      }
      const docFolder = get().documents[folder] ?? [];
      const page = docFolder
        .sort((a, b) => {
          const valueA = a[sortBy as keyof DocumentSnapshotDto];
          const valueB = b[sortBy as keyof DocumentSnapshotDto];

          // Handle undefined values
          if (valueA === undefined && valueB === undefined) return 0;
          if (valueA === undefined) return descending ? 1 : -1;
          if (valueB === undefined) return descending ? -1 : 1;

          // Compare based on descending or ascending order
          if (valueA < valueB) return descending ? 1 : -1;
          if (valueA > valueB) return descending ? -1 : 1;

          return 0; // If both values are equal
        })
        .filter((doc) =>
          doc.name?.toLowerCase().includes(prefilter.toLowerCase())
        )
        .slice(skip, skip + top);

      return {
        documents: page,
        availableItemsCount: docFolder.filter((doc) =>
          doc.name?.toLowerCase().includes(prefilter.toLowerCase())
        ).length,
      };
    },
    clearDocumentsStore: () => {
      clearDocuments(set);
    },
    documents: {} as { [key in DocumentFolder]: DocumentSnapshotDto[] },
    isUpdating: false,
    isInitializing: false,
    isContextUpdating: true,
    getAllInboxDocumentsPage: async (
      country,
      top: number = 100,
      skip: number = 0,
      sortBy: string = 'creationDate',
      descending: boolean = true
    ): Promise<DocumentSnapshotDTORetrievalResultWithUnread | null> => {
      let unreadItemsCount = 0;
      if (get().isInitializing) {
        await initializeDocuments;
      }
      if (country === undefined || country === '' || country === null) {
        return {
          documents: [],
          availableItemsCount: 0,
          unreadItemsCount: 0,
        };
      }
      const myContractDocuments =
        get().documents[DocumentFolder.MyContract] ?? [];
      const myPaymentsDocuments =
        get().documents[DocumentFolder.MyPayments] ?? [];
      const myUploadDocuments =
        get().documents[DocumentFolder.MyDataExportSupportData] ?? [];
      let myInboxDocuments = myContractDocuments
        .concat(myPaymentsDocuments)
        .concat(myUploadDocuments);

      if (country == 'FI') {
        const myNewslettersDocuments =
          get().documents[DocumentFolder.MyNewsletter] ?? [];
        myInboxDocuments = myInboxDocuments.concat(myNewslettersDocuments);
      }
      if (country == 'DE') {
        const myNewslettersDocuments =
          get().documents[DocumentFolder.MyNewsletter] ?? [];
        const myBusinessInfoBeetWithFilter =
          get().documents[DocumentFolder.MyBusinessInfoBeet]?.filter((doc) =>
            doc.name?.toLowerCase().includes('anbauempfehlung')
          ) ?? [];
        myInboxDocuments = myInboxDocuments
          .concat(myNewslettersDocuments)
          .concat(myBusinessInfoBeetWithFilter);
      }
      unreadItemsCount = myInboxDocuments.filter((doc) => !doc.read).length;
      const page = myInboxDocuments
        .sort((a, b) => {
          const valueA = a[sortBy as keyof DocumentSnapshotDto];
          const valueB = b[sortBy as keyof DocumentSnapshotDto];

          // Handle undefined values
          if (valueA === undefined && valueB === undefined) return 0;
          if (valueA === undefined) return descending ? 1 : -1;
          if (valueB === undefined) return descending ? -1 : 1;

          // Compare based on descending or ascending order
          if (valueA < valueB) return descending ? 1 : -1;
          if (valueA > valueB) return descending ? -1 : 1;

          return 0; // If both values are equal
        })
        .slice(skip, skip + top);

      return {
        documents: page,
        availableItemsCount: myInboxDocuments.length,
        unreadItemsCount: unreadItemsCount,
      };
    },
  }))
);
